import { LoadingOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { Input, Select, Tabs, Drawer, Checkbox, Button, Collapse } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CourseProgress from "../Components/CourseProgress";
import TypeOfCourses from "../Components/Courses/TypeOfCourses";
import { getCourseraContents, GetFilteratioData } from "../slices/courseraSlice";

const { Panel } = Collapse;

function CourseTab() {
  const dispatch = useDispatch();
  const courseData = useSelector((state) => state.coursera.courseData);
  const filterData = useSelector((state) => state.coursera.filtersData);
  const [start, setStart] = useState(0);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [resData, setResData] = useState([]);
  const [courseSearch, setCourseSearch] = useState("");
  const [searchType, setSearchType] = useState([]);
  const [difficulty, setDifficulty] = useState([]);
  const [skills, setSkills] = useState([]);
  const [domains, setDomains] = useState([]);
  const [subDomains, setSubDomains] = useState([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  const difficultyOptions = filterData?.difficultyLevel || [];
  const skillsOptions = filterData?.skills || [];
  const typeOptions = filterData?.type || [];
  const domainsOptions = filterData?.domains || [];
  const subDomainsOptions = filterData?.subDomains || [];

  useEffect(() => {
    setLoading(true);
    dispatch(GetFilteratioData());
    dispatch(
      getCourseraContents({
        start,
        limit,
        name: courseSearch,
        difficultyLevel: difficulty,
        type: searchType,
        skills,
        domains,
        subDomains
      })
    ).then((res) => {
      const data = res?.payload?.data?.elements || [];
      const newData = start === 0 ? data : resData.concat(data);
      setResData(newData);
      setLoading(false);
    });
  }, [start, limit, courseSearch, searchType, difficulty, skills, domains, subDomains, dispatch]);

  const handleFiltersChange = (setFunction) => (values) => {
    setFunction(values);
    setStart(0);
    setLimit(1000);
  };

  const loadMore = () => {
    setLoading(true);
    setStart((prev) => prev + 10);
    setLoading(false);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setCourseSearch(value);
    setStart(0);
    setLimit(1000);
  };

  const debouncedHandleSearch = useCallback(debounce(handleSearch, 100), []);

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      setLoading(false);
    };
  }

  const handleOpenDrawer = () => {
    setIsDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
  };

  const clearFilters = () => {
    setSearchType([]);
    setDifficulty([]);
    setSkills([]);
    setDomains([]);
    setSubDomains([]);
    setStart(0);
    setLimit(10);
  };

  return (
    <>
      <div className="courses-background mt-8 flex flex-col gap-4">
        <h1 className="text-pureWhite text-[36px] font-bizmo font-semibold">Courses</h1>
        <p className="text-pureWhite text-center w-[384px] text-sm">
          Embark on Your Learning Journey: Discover, Dive In, and Excel with Our Curated Collection of
        </p>
      </div>

      <div className="mt-8 overflow-hidden">
        <div className="flex gap-x-5 p-4">
          <Button
            icon={<FilterOutlined />}
            onClick={handleOpenDrawer}
            className="bg-pureWhite rounded-3xl border-none px-5 py-2 w-36"
          >
            Filters
          </Button>
          <Input
            className="bg-pureWhite rounded-3xl border-none px-5 py-2 "
            placeholder="Search"
            suffix={<SearchOutlined />}
            onChange={debouncedHandleSearch}
          />
        </div>
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane key="1" tab="All Courses">
            <>
              <TypeOfCourses
                setLoading={setLoading}
                setResData={setResData}
                loadMore={loadMore}
                loading={loading}
                courseData={resData}
                total={courseData?.paging?.total}
                title="Compulsory Courses"
                courseSearch={courseSearch}
              />
              <div className="text-center my-10">
                {courseData?.paging?.total <= resData.length ? (
                  <h1 className="bg-signupTextBg inline-block text-transparent bg-clip-text font-semibold text-lg">
                    No additional data to display.
                  </h1>
                ) : (
                  !courseSearch && (
                    <>
                      {courseData?.elements?.length && (
                        <button
                          className={`m-auto my-10 flex items-center justify-center gap-x-4 py-2 px-6 bg-gradient-to-l from-darkerOrange via-darkOrange to-lightOrange hover:bg-gradient-to-bl text-white drop-shadow-md rounded-full`}
                          onClick={loadMore}
                        >
                          Load More
                          {loading && <LoadingOutlined />}
                        </button>
                      )}
                    </>
                  )
                )}
              </div>
            </>
          </Tabs.TabPane>
          <Tabs.TabPane key="2" tab="Enroll Courses">
            <CourseProgress />
          </Tabs.TabPane>
        </Tabs>
      </div>

      <Drawer
        title="Filter Courses"
        placement="right"
        closable={false}
        onClose={handleDrawerClose}
        visible={isDrawerVisible}
        width={300}
        extra={
          <Button onClick={clearFilters} type="link">
            Clear Filters
          </Button>
        }
      >
        <Collapse accordion>
          <Panel header="Difficulty Level" key="1">
            <Checkbox.Group
              options={difficultyOptions.map(val => ({ label: val, value: val }))}
              onChange={handleFiltersChange(setDifficulty)}
              value={difficulty}
            />
          </Panel>
          <Panel header="Skills" key="2">
            <Checkbox.Group
              options={skillsOptions.map(val => ({ label: <p className="flex flex-col uppercase text-orange2">{val}</p>, value: val }))}
              onChange={handleFiltersChange(setSkills)}
              value={skills}
            />
          </Panel>
          <Panel header="Type" key="3">
            <Checkbox.Group
              options={typeOptions.map(val => ({ label: <p className="flex flex-col uppercase text-orange2">{val}</p>, value: val }))}
              onChange={handleFiltersChange(setSearchType)}
              value={searchType}
            />
          </Panel>
          <Panel header="Domains" key="4">
            <Checkbox.Group
              options={domainsOptions.map(val => ({ label: <p className="flex flex-col uppercase text-orange2">{val}</p>, value: val }))}
              onChange={handleFiltersChange(setDomains)}
              value={domains}
            />
          </Panel>
          <Panel header="Sub Domains" key="5">
            <Checkbox.Group
              options={subDomainsOptions.map(val => ({ label: <p className="flex flex-col uppercase text-orange2">{val}</p>, value: val }))}
              onChange={handleFiltersChange(setSubDomains)}
              value={subDomains}
            />
          </Panel>
        </Collapse>
      </Drawer>
    </>
  );
}

export default CourseTab;
